import React, { useState, useEffect } from 'react';
import {
  MessageCircle,
  Users,
  Bot,
  Settings,
  Search,
  Filter,
  MoreVertical,
  Reply,
  Archive,
  Star,
  Clock,
  CheckCircle,
  AlertCircle,
  Phone,
  Mail,
  MessageSquare,
  Zap,
  TrendingUp,
  Eye
} from 'lucide-react';

const ChatManagement = () => {
  const [activeTab, setActiveTab] = useState('conversations');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedConversation, setSelectedConversation] = useState(null);

  // Mock chat data - replace with real data from your backend
  const [conversations, setConversations] = useState([
    {
      id: 1,
      user: {
        name: 'John Doe',
        email: 'john@example.com',
        avatar: null,
        status: 'online'
      },
      lastMessage: 'Hi, I need help with my Arduino project',
      timestamp: new Date(Date.now() - 5 * 60 * 1000),
      unread: 2,
      status: 'active',
      channel: 'ai-chat',
      priority: 'normal'
    },
    {
      id: 2,
      user: {
        name: 'Jane Smith',
        email: 'jane@example.com',
        avatar: null,
        status: 'offline'
      },
      lastMessage: 'When will my order be shipped?',
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      unread: 0,
      status: 'resolved',
      channel: 'whatsapp',
      priority: 'high'
    },
    {
      id: 3,
      user: {
        name: 'Mike Johnson',
        email: 'mike@example.com',
        avatar: null,
        status: 'online'
      },
      lastMessage: 'Can you recommend sensors for my project?',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      unread: 1,
      status: 'pending',
      channel: 'support',
      priority: 'normal'
    }
  ]);

  const [chatStats, setChatStats] = useState({
    totalConversations: 156,
    activeChats: 23,
    resolvedToday: 45,
    avgResponseTime: '2.5 min',
    satisfactionRate: 94,
    aiHandledChats: 78
  });

  const [aiSettings, setAiSettings] = useState({
    enabled: true,
    autoRespond: true,
    handoffThreshold: 3,
    workingHours: {
      enabled: true,
      start: '09:00',
      end: '18:00'
    },
    greetingMessage: 'Hello! How can I help you today?',
    fallbackMessage: 'I\'ll connect you with a human agent shortly.'
  });

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conv.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || conv.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'ai-chat': return <Bot size={16} className="text-blue-500" />;
      case 'whatsapp': return <MessageSquare size={16} className="text-green-500" />;
      case 'support': return <Mail size={16} className="text-purple-500" />;
      default: return <MessageCircle size={16} className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-red-500';
      case 'normal': return 'border-l-blue-500';
      case 'low': return 'border-l-gray-500';
      default: return 'border-l-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Chat Management</h1>
          <p className="text-gray-600 mt-1">Manage customer conversations and AI chat settings</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center">
            <Settings size={16} className="mr-2" />
            Chat Settings
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Chats</p>
              <p className="text-2xl font-bold text-blue-900">{chatStats.totalConversations}</p>
            </div>
            <MessageCircle className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Active Now</p>
              <p className="text-2xl font-bold text-green-900">{chatStats.activeChats}</p>
            </div>
            <Users className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Resolved Today</p>
              <p className="text-2xl font-bold text-purple-900">{chatStats.resolvedToday}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">Avg Response</p>
              <p className="text-2xl font-bold text-yellow-900">{chatStats.avgResponseTime}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-indigo-600">Satisfaction</p>
              <p className="text-2xl font-bold text-indigo-900">{chatStats.satisfactionRate}%</p>
            </div>
            <Star className="w-8 h-8 text-indigo-500" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-4 border border-cyan-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-cyan-600">AI Handled</p>
              <p className="text-2xl font-bold text-cyan-900">{chatStats.aiHandledChats}%</p>
            </div>
            <Bot className="w-8 h-8 text-cyan-500" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'conversations', label: 'Conversations', icon: MessageCircle },
              { id: 'ai-settings', label: 'AI Settings', icon: Bot },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'conversations' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      placeholder="Search conversations..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>

                <button className="flex items-center space-x-2 text-gray-600 hover:text-gray-800">
                  <Filter size={16} />
                  <span>More Filters</span>
                </button>
              </div>

              {/* Conversations List */}
              <div className="space-y-3">
                {filteredConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    className={`bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer border-l-4 ${getPriorityColor(conversation.priority)}`}
                    onClick={() => setSelectedConversation(conversation)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">
                              {conversation.user.name.charAt(0)}
                            </span>
                          </div>
                          {conversation.user.status === 'online' && (
                            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-medium text-gray-900">{conversation.user.name}</h4>
                            {getChannelIcon(conversation.channel)}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(conversation.status)}`}>
                              {conversation.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 truncate">{conversation.lastMessage}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{formatTime(conversation.timestamp)}</p>
                          {conversation.unread > 0 && (
                            <span className="inline-block bg-blue-500 text-white text-xs rounded-full px-2 py-1 mt-1">
                              {conversation.unread}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <button className="p-1 text-gray-400 hover:text-blue-600">
                            <Reply size={16} />
                          </button>
                          <button className="p-1 text-gray-400 hover:text-yellow-600">
                            <Star size={16} />
                          </button>
                          <button className="p-1 text-gray-400 hover:text-gray-600">
                            <MoreVertical size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'ai-settings' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* AI Configuration */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">AI Configuration</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">Enable AI Chat</label>
                      <input
                        type="checkbox"
                        checked={aiSettings.enabled}
                        onChange={(e) => setAiSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">Auto-respond to queries</label>
                      <input
                        type="checkbox"
                        checked={aiSettings.autoRespond}
                        onChange={(e) => setAiSettings(prev => ({ ...prev, autoRespond: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Handoff Threshold (failed attempts)
                      </label>
                      <input
                        type="number"
                        value={aiSettings.handoffThreshold}
                        onChange={(e) => setAiSettings(prev => ({ ...prev, handoffThreshold: parseInt(e.target.value) }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="1"
                        max="10"
                      />
                    </div>
                  </div>
                </div>

                {/* Working Hours */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Working Hours</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">Enable working hours</label>
                      <input
                        type="checkbox"
                        checked={aiSettings.workingHours.enabled}
                        onChange={(e) => setAiSettings(prev => ({
                          ...prev,
                          workingHours: { ...prev.workingHours, enabled: e.target.checked }
                        }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                        <input
                          type="time"
                          value={aiSettings.workingHours.start}
                          onChange={(e) => setAiSettings(prev => ({
                            ...prev,
                            workingHours: { ...prev.workingHours, start: e.target.value }
                          }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                        <input
                          type="time"
                          value={aiSettings.workingHours.end}
                          onChange={(e) => setAiSettings(prev => ({
                            ...prev,
                            workingHours: { ...prev.workingHours, end: e.target.value }
                          }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">AI Messages</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Greeting Message</label>
                    <textarea
                      value={aiSettings.greetingMessage}
                      onChange={(e) => setAiSettings(prev => ({ ...prev, greetingMessage: e.target.value }))}
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fallback Message</label>
                    <textarea
                      value={aiSettings.fallbackMessage}
                      onChange={(e) => setAiSettings(prev => ({ ...prev, fallbackMessage: e.target.value }))}
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  Save AI Settings
                </button>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="text-center py-12">
                <TrendingUp size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Chat Analytics</h3>
                <p className="text-gray-600">Detailed analytics and reporting coming soon</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatManagement;
